'use client';

import useIsMobile from '@/hooks/use-is-mobile';
import { ADD_CHARACTER_CARDS } from '@/typed-graphql/mutation';
import { gql, useMutation } from '@apollo/client';
import clsx from 'clsx';
import FileSaver from 'file-saver';
import Link from 'next/link';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AspectRatio } from '../ui/aspect-ratio';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';

const enum EnumStep {
  Init,
  GetNow,
  GetMyCharacterCard,
  ChooseEventTheme,
  ChooseDressStyle,
  DrawDress,
}

const WhoIsYourCharacterPage = () => {
  const isMobile = useIsMobile();

  const [step, setStep] = useState(EnumStep.ChooseDressStyle);
  const [selectedAnswerIndex, setSelectedAnswerIndex] = useState(1);
  const [selectedDressIndex, setSelectedDressIndex] = useState(-1);
  const [renderSelectedDressIndex, setRenderSelectedDressIndex] = useState(-1);
  const [btnAvailable, setBtnAvailable] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [loadingPercent, setLoadingPercent] = useState(100);
  const [reqAddCharacterCards] = useMutation(gql(ADD_CHARACTER_CARDS));
  const [startCardAnimation, setStartCardAnimation] = useState(false);

  const selectedCardSrc =
    selectedAnswerIndex === -1 || renderSelectedDressIndex === -1
      ? ''
      : configs[selectedAnswerIndex].images[renderSelectedDressIndex].cardSrc;
  const productUrl =
    selectedAnswerIndex === -1 || renderSelectedDressIndex === -1
      ? ''
      : configs[selectedAnswerIndex].images[renderSelectedDressIndex].productUrl;

  const loadingImages = useCallback(async (imgs: string[]) => {
    if (!imgs.length) return;

    const singleImagePercent = Math.ceil(100 / imgs.length);

    setLoadingPercent(0);
    await Promise.all(
      imgs.map(src => {
        if (!src) {
          return new Promise<void>(resolve => {
            resolve();
          });
        }

        return new Promise<void>(resolve => {
          const image = new window.Image();
          image.onload = () => {
            resolve();
          };
          image.onerror = () => {
            resolve();
          };

          image.src = src;

          if (image.complete) {
            resolve();
          }
        }).then(() => {
          setLoadingPercent(x => Math.min(100, x + singleImagePercent));
        });
      }),
    );
    setLoadingPercent(100);
  }, []);

  useEffect(() => {
    (async () => {
      setStep(EnumStep.Init);
      await loadingImages(
        [
          process.env.NEXT_PUBLIC_BASE_PATH +
            (isMobile
              ? '/images/who-is-your-character/step-1-bg-m.webp'
              : '/images/who-is-your-character/step-1-bg.webp'),
          process.env.NEXT_PUBLIC_BASE_PATH +
            (isMobile ? '/images/who-is-your-character/gray-bg-m.webp' : '/images/who-is-your-character/gray-bg.webp'),
          isMobile
            ? ''
            : process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/card-animation/left-hand.webp',
          process.env.NEXT_PUBLIC_BASE_PATH +
            `/images/who-is-your-character/card-animation/card-1${isMobile ? '-m' : ''}.webp`,
          process.env.NEXT_PUBLIC_BASE_PATH +
            `/images/who-is-your-character/card-animation/card-2${isMobile ? '-m' : ''}.webp`,
          process.env.NEXT_PUBLIC_BASE_PATH +
            `/images/who-is-your-character/card-animation/card-3${isMobile ? '-m' : ''}.webp`,
          process.env.NEXT_PUBLIC_BASE_PATH +
            `/images/who-is-your-character/card-animation/card-4${isMobile ? '-m' : ''}.webp`,
          process.env.NEXT_PUBLIC_BASE_PATH +
            `/images/who-is-your-character/card-animation/card-5${isMobile ? '-m' : ''}.webp`,
          process.env.NEXT_PUBLIC_BASE_PATH +
            `/images/who-is-your-character/card-animation/card-6${isMobile ? '-m' : ''}.webp`,
          isMobile
            ? ''
            : process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/card-animation/right-hand.webp',
        ].filter(Boolean),
      );
      setStep(EnumStep.GetNow);
    })();
  }, [loadingImages, isMobile]);

  const handleSelectAnswer = async (answerIndex: number) => {
    const inputField = [
      'queen',
      'fashionista',
      'enchantress',
      'diva',
      'adventurer',
      'empress',
      'free_spirit',
      'trendsetter',
    ][answerIndex];

    reqAddCharacterCards({
      variables: {
        input: {
          [inputField]: 1,
        },
      },
    }).catch(error => {
      if (process.env.NODE_ENV === 'development') {
        console.log(error);
      }
    });

    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    await loadingImages([
      process.env.NEXT_PUBLIC_BASE_PATH +
        (isMobile
          ? '/images/who-is-your-character/card-placeholder-m.webp'
          : '/images/who-is-your-character/card-placeholder.webp'),
      isMobile ? configs[answerIndex].mobileLichtSrc : configs[answerIndex].lichtSrc,
      process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/card.webp',
      ...configs[answerIndex].images.map(x => x.cardSrc),
      ...configs[answerIndex].images.map(x => x.placeholderSrc),
    ]);

    setSelectedAnswerIndex(answerIndex);
    setStep(EnumStep.DrawDress);
    setSelectedDressIndex(0);
    setTimeout(() => {
      setRenderSelectedDressIndex(0);
    }, 300);
    setTimeout(() => {
      setBtnAvailable(true);
    }, 2400);
  };

  return (
    <>
      {createPortal(
        <>
          <style>
            {`
            @font-face {
              font-family: 'inter';
              font-style: normal;
              font-weight: 300;
              font-display: swap;
              src: url(${process.env.NEXT_PUBLIC_BASE_PATH}/fonts/inter-light.ttf) format('truetype');
            }

            @font-face {
              font-family: 'fogtwono5';
              font-style: normal;
              font-weight: 400;
              font-display: swap;
              src: url(${process.env.NEXT_PUBLIC_BASE_PATH}/fonts/fogtwono5.otf) format('opentype');
            }

            #container {
              min-height: auto;
              display: flex;
              flex-direction: column;
            }

            @supports (height: 100dvh) {
              #container {
                min-height: auto;
              }
            }

            @media (min-width: 960px) {
              #container {
                min-height: auto;
              }
            }

          `}
          </style>
        </>,
        document.head,
      )}
      <div
        className={clsx(
          'flex-1  overflow-auto bg-[linear-gradient(180deg,#FCEEED_0%,#E3F0F6_100%)] lg_bg-none',
          (step === EnumStep.GetMyCharacterCard ||
            step === EnumStep.ChooseEventTheme ||
            step === EnumStep.ChooseDressStyle) &&
            'flex items-center justify-center',
          step === EnumStep.DrawDress && 'lg_flex lg_items-center lg_justify-center',
          'relative',
          'bg-[length:100%_auto,cover] bg-fixed bg-no-repeat',
        )}
        style={{
          backgroundImage:
            (isMobile && step === EnumStep.GetNow) || !isMobile
              ? `url(${process.env.NEXT_PUBLIC_BASE_PATH + (isMobile ? '/images/who-is-your-character/step-1-bg-m.webp' : '/images/who-is-your-character/step-1-bg.webp')}),url(${process.env.NEXT_PUBLIC_BASE_PATH + (isMobile ? '/images/who-is-your-character/gray-bg-m.webp' : '/images/who-is-your-character/gray-bg.webp')})`
              : undefined,
        }}
      >
        {loadingPercent !== 100 ? (
          <div className='absolute z-50 flex h-full w-full flex-col  items-center justify-center gap-y-2.5 bg-white/40'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='101'
              height='65'
              fill='none'
              viewBox='0 0 101 65'
              className='animate-pulse'
            >
              <g
                fill='#80583F'
                fillRule='evenodd'
                clipRule='evenodd'
                opacity='0.5'
              >
                <path d='M50.526 46.853c1.25-.118 3.05-1.059 3.951-2.088.988-1.118 1.453-2.53.174-3.5-1.017-.765-2.615-.118-3.312.559-.813.794-1.307 2.529-3.312 2.647-2.295.147-3.719-1.971-4.358-5.383-.03-.147-.058-.323-.087-.5-.32-2.03-.378-4.47-.175-7.147.059-.941.175-2.117.32-3.412.116-.97.407-2.911.784-5.794a16.5 16.5 0 0 0-3.196 1.236c-.348.206-.726.44-1.162.706l-.93.646a337 337 0 0 0-.523 4.5c-.958.089-8.658.089-11.273 0 3.429-5.294 7.787-12 14.411-20.911-.464 1.882-1.075 5.265-1.772 10.176 1.395-.647 2.935-1.176 4.678-1.647l.523-.147c.232-1.735.494-3.676.784-5.794.465-3.147.872-5.676 1.046-6.853.262-1.382.581-2.794.93-4.147-2.993 2.588-6.712 2.441-11.505 2.794-9.443.794-15.05 5.735-15.05 12.235 0 2.294.958 3.677 2.614 3.677 1.25 0 2.121-1.118 2.121-2.059 0-1.265-1.017-2.441-1.017-3.823 0-1.706 1.104-3.677 2.76-5 3.051-2.442 7.787-2.589 14.208-3.177-12.871 14.647-20.542 31.824-30.217 41.647-1.975 2-3.777 3.235-5.752 3.235-1.395 0-3.138-.676-4.068-.676C.726 48.853 0 49.794 0 50.97c0 1.764 1.453 2.735 4.155 2.735 6.479 0 12.87-8.382 21.674-22 3.458-.059 11.041 0 12.959 0v.941c.116 2.824.726 5.882 1.772 8.059 2.528 5.265 5.84 6.53 9.966 6.147'></path>
                <path d='M30.507 32.969a13 13 0 0 0-.232 2.294c0 2.294 1.017 4.5 2.76 4.5 1.25 0 1.976-1.03 1.976-2.206 0-1.235-1.424-2.47-1.627-4.559zM32.047 28.91h2.8179999999999996c1.279-2.411 3.341-4.764 5.927-6.147 3.487-1.853 7.438-2.323 7.438-2.323s-.871 2.794-1.569 6.647c-.842 4.647-1.51 9.53-1.51 12.941.29.853.697 1.765 1.103 2.265.669.794 1.22.912 1.599.912h.145c.494-.03.813-.236 1.191-.618.29-.265.64-.735.959-1.177 0-.44.029-.853.058-1.294.349-5.059.755-9.823 2.005-15.176 1.22-5.147 3.108-10.088 3.108-10.088s-1.917 1.411-4.067 1.94c-3.138.765-9.443 1.912-14.266 5.471-2.15 1.618-3.806 4.236-4.94 6.647M96.926 10.414c-1.366 0-2.47.97-2.47 2.294 0 1.47 1.54 3.088 1.54 6.412 0 4.559-2.847 9.941-7.496 17.03-5.84 8.764-11.07 14.588-14.353 18.794.058-8.295.087-17.471.697-24.942.61-7.47 1.83-13.264 1.83-13.264s-4.387 3.235-7.67 8.176c-3.748 5.588-7.409 12.559-10.576 18-3.922 6.794-7.118 11.706-7.118 11.706s-.523-4.03-.872-6.441c-.406 0-.813.03-1.19.03-1.395 0-2.645-.236-3.778-.677.407 6.176 1.133 11.794 1.889 16.53C61.857 46.237 66.68 31.943 70.893 26.62A103.4 103.4 0 0 0 69.44 41.06l.349 22.059c5.753-5.53 10.576-11.735 17.055-20.353 8.658-11.559 13.307-20.265 13.307-26.324-.029-3.794-1.191-6.029-3.225-6.029'></path>
              </g>
            </svg>
            <span className='text-sm text-80583F lg_text-base'>Loading {loadingPercent}%</span>
          </div>
        ) : null}
        {step === EnumStep.GetNow ? (
          <div className='pt-[51px] lg_pt-[100px]'>
            <h1 className='px-4 text-center font-fogtwono5 text-[32px] font-bold leading-[1.2] text-[#2469A9] lg_px-0 lg_text-[60px]'>
              WHO IS YOUR PERFECT CHARACTER?
            </h1>
            <div className='mt-[39px] lg_mt-0'>
              <AspectRatio ratio={isMobile ? 375 / 168 : 1920 / 711}>
                <img
                  src={
                    process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/card-animation/left-hand.webp'
                  }
                  width={592}
                  height={567}
                  alt=''
                  className='absolute bottom-0 left-0 z-10 hidden h-auto w-[30.78%] animate-in slide-in-from-left-full [animation-duration:600ms] lg_block'
                  onAnimationEnd={() => {
                    if (isMobile) return;

                    setStartCardAnimation(true);
                  }}
                />
                <picture>
                  <source
                    media='(max-width: 959px)'
                    srcSet={
                      process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/card-animation/card-1-m.webp'
                    }
                    width={191}
                    height={224}
                  />
                  <source
                    media='(min-width: 960px)'
                    srcSet={
                      process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/card-animation/card-1.webp'
                    }
                    width={284}
                    height={333}
                  />
                  <img
                    alt=''
                    className={clsx(
                      'absolute bottom-0 left-[2.4%] z-[9] h-auto w-[25.33%] lg_left-[21.35%] lg_top-[32.20%] lg_w-[14.79%] ',
                      startCardAnimation ? 'lg_opacity-100' : 'lg_opacity-0',
                      'ease-in-out lg_transition-opacity lg_[transition-duration:200ms]',
                      'animate-in fade-in-0 [animation-duration:500ms] ',
                    )}
                    onAnimationEnd={() => {
                      if (!isMobile) return;

                      setStartCardAnimation(true);
                    }}
                  />
                </picture>

                <picture>
                  <source
                    media='(max-width: 959px)'
                    srcSet={
                      process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/card-animation/card-2-m.webp'
                    }
                    width={152}
                    height={211}
                  />
                  <source
                    media='(min-width: 960px)'
                    srcSet={
                      process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/card-animation/card-2.webp'
                    }
                    width={226}
                    height={215}
                  />
                  <img
                    alt=''
                    className={clsx(
                      'absolute  z-[8] h-auto w-[20%] lg_w-[11.77%]',
                      startCardAnimation
                        ? 'left-[17.33%] top-[11.9%] opacity-100 lg_left-[30.26%] lg_top-[17.3%] lg_rotate-0'
                        : 'left-[5.2%] top-[38%] rotate-[-15deg] opacity-0 lg_left-[23%] lg_top-[34%] lg_rotate-[-14deg]',
                      'transition-all ease-in-out [transition-delay:200ms] [transition-duration:500ms]',
                    )}
                  />
                </picture>

                <picture>
                  <source
                    media='(max-width: 959px)'
                    srcSet={
                      process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/card-animation/card-3-m.webp'
                    }
                    width={119}
                    height={192}
                  />
                  <source
                    media='(min-width: 960px)'
                    srcSet={
                      process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/card-animation/card-3.webp'
                    }
                    width={177}
                    height={285}
                  />
                  <img
                    alt=''
                    className={clsx(
                      'absolute  z-[7] h-auto w-[15.73%] lg_w-[9.218%] ',
                      startCardAnimation
                        ? 'left-[34.4%] top-[4.16%] rotate-0 opacity-100 lg_left-[40.16%] lg_top-[11.67%]'
                        : 'left-[7%] top-[42%] rotate-[-29deg] opacity-0 lg_left-[24%] lg_top-[36%] lg_rotate-[-29deg]',
                      ' transition-all ease-in-out [transition-duration:500ms] [transition-delay:250ms]',
                    )}
                  />
                </picture>

                <picture>
                  <source
                    media='(max-width: 959px)'
                    srcSet={
                      process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/card-animation/card-4-m.webp'
                    }
                    width={158}
                    height={210}
                  />
                  <source
                    media='(min-width: 960px)'
                    srcSet={
                      process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/card-animation/card-4.webp'
                    }
                    width={235}
                    height={313}
                  />
                  <img
                    alt=''
                    className={clsx(
                      'absolute  z-[6] h-auto w-[20.8%] lg_w-[12.24%] ',
                      startCardAnimation
                        ? 'left-[47.47%] top-0 rotate-0 opacity-100 lg_left-[47.6%] lg_top-[8.58%]'
                        : 'left-[4%] top-[39%]  rotate-[-43deg] opacity-0 lg_left-[22.8%] lg_top-[32.9%] lg_rotate-[-43deg]',
                      ' transition-all ease-in-out [transition-duration:500ms]  [transition-delay:300ms]',
                    )}
                  />
                </picture>

                <picture>
                  <source
                    media='(max-width: 959px)'
                    srcSet={
                      process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/card-animation/card-5-m.webp'
                    }
                    width={184}
                    height={216}
                  />
                  <source
                    media='(min-width: 960px)'
                    srcSet={
                      process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/card-animation/card-5.webp'
                    }
                    width={276}
                    height={324}
                  />
                  <img
                    alt=''
                    className={clsx(
                      'absolute  z-[5] h-auto w-[24.53%] lg_w-[14.375%] ',
                      startCardAnimation
                        ? 'left-[59.73%]  top-[5.95%] rotate-0 opacity-100 lg_left-[54.84%] lg_top-[12.8%]'
                        : 'left-[2%] top-[37%] rotate-[-57deg] opacity-0 lg_left-[32.9%] lg_top-[21.8%] lg_rotate-[-60deg]',
                      ' transition-all ease-in-out [transition-duration:500ms]  [transition-delay:350ms]',
                    )}
                  />
                </picture>

                <picture>
                  <source
                    media='(max-width: 959px)'
                    srcSet={
                      process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/card-animation/card-6-m.webp'
                    }
                    width={201}
                    height={211}
                  />
                  <source
                    media='(min-width: 960px)'
                    srcSet={
                      process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/card-animation/card-6.webp'
                    }
                    width={299}
                    height={314}
                  />
                  <img
                    alt=''
                    className={clsx(
                      'absolute  z-[4] h-auto w-[26.67%] lg_w-[15.57%] ',
                      startCardAnimation
                        ? 'left-[70.93%] top-[19.64%] rotate-0 opacity-100 lg_left-[61.4%] lg_top-[22.78%]'
                        : 'left-[1%] top-[37%] rotate-[-75deg] opacity-0 lg_left-[20.9%] lg_top-[32.8%] lg_rotate-[-72deg]',
                      ' transition-all ease-in-out [transition-duration:500ms]  [transition-delay:400ms]',
                    )}
                  />
                </picture>

                <div className='absolute right-0 top-0 z-[5] hidden h-auto w-[29.48%] overflow-hidden lg_block'>
                  <AspectRatio ratio={567 / 396}>
                    <img
                      src={
                        process.env.NEXT_PUBLIC_BASE_PATH +
                        '/images/who-is-your-character/card-animation/right-hand.webp'
                      }
                      width={567}
                      height={396}
                      alt=''
                      className={clsx(
                        'h-auto w-full',
                        startCardAnimation ? 'translate-x-[60px]' : 'translate-x-full',
                        'transition-all duration-4xl ease-in-out [transition-delay:600ms]',
                      )}
                    />
                  </AspectRatio>
                </div>
              </AspectRatio>
            </div>

            <div className='mt-[78px] flex flex-col flex-wrap items-center lg_mt-0 lg_-translate-y-[60px] lg_flex-row lg_justify-center lg_gap-x-[128px]'>
              <div className='flex justify-end lg_min-w-[400px]'>
                <div className='order-1 text-center font-fogtwono5 text-[15px] leading-[1.2] text-[#2469A9] lg_order-1 lg_max-w-[287px] lg_text-right lg_text-[40px]'>
                  GET UNIQUE CHARACTER CARD
                </div>
              </div>
              <div className='order-2 mt-2 text-center font-fogtwono5 text-[15px] leading-[1.2] text-[#EB67AC] lg_order-3 lg_mt-0 lg_max-w-[400px] lg_text-left lg_text-[40px]'>
                BASED ON EVENT THEMES OR DRESS STYLES
              </div>
              <button
                type='button'
                className='group relative order-3 mt-5 lg_order-2 lg_mt-0'
                onClick={() => {
                  setStep(EnumStep.GetMyCharacterCard);
                  document.documentElement.scrollTop = 0;
                  document.body.scrollTop = 0;
                }}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='273'
                  height='49'
                  fill='none'
                  viewBox='0 0 273 49'
                  className='lg_hidden'
                >
                  <path
                    fill='#2469A9'
                    d='M262.692 0v2.074c0 3.716 3.367 6.74 7.599 6.74h2.404v31.37h-2.308c-4.232 0-7.599 3.026-7.599 6.742V49H9.908v-2.074c0-3.716-3.463-6.741-7.6-6.741H0V8.815h2.309c4.232 0 7.598-3.025 7.598-6.74V0zM10.966.95v1.124c0 4.235-3.848 7.692-8.657 7.692H1.058v29.468h1.25c4.81 0 8.658 3.457 8.658 7.692v1.123h250.668v-1.123c0-4.235 3.944-7.692 8.657-7.692h1.25V9.766h-1.25c-4.809 0-8.657-3.457-8.657-7.692V.95zm248.552 1.894.289 1.296c.865 3.63 4.135 6.482 8.271 7.26l1.443.258v25.667l-1.443.259c-4.136.864-7.406 3.716-8.271 7.346l-.289 1.297H13.274l-.289-1.297c-.962-3.716-4.136-6.568-8.272-7.346l-1.442-.259v-25.58l1.442-.26c4.136-.864 7.407-3.716 8.272-7.345l.29-1.296z'
                  ></path>
                </svg>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='234'
                  height='69'
                  fill='none'
                  viewBox='0 0 234 69'
                  className='hidden lg_block'
                >
                  <path
                    className='fill-[#2469A9] lg_group-hover_fill-[#EB67AC]'
                    d='M231 12.382c-5.2 0-9.5-4.294-9.5-9.486V0H12.4v2.896c0 5.192-4.3 9.486-9.5 9.486H0v44.236h2.9c5.2 0 9.5 4.294 9.5 9.486V69h209.1v-2.896c0-5.192 4.3-9.486 9.5-9.486h2.9V12.382zm1.6 42.938H231c-6 0-10.8 4.893-10.8 10.784v1.598H13.7v-1.598c0-5.991-4.9-10.784-10.8-10.784H1.3V13.68h1.6c6 0 10.8-4.893 10.8-10.784V1.298h206.5v1.598c0 5.991 4.9 10.784 10.8 10.784h1.6z'
                  ></path>
                </svg>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='226'
                  height='61'
                  fill='none'
                  viewBox='0 0 226 61'
                  className='absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 lg_block'
                >
                  <path
                    className='fill-[#2469A9] lg_group-hover_fill-[#EB67AC]'
                    d='M12.1 1.797C11 6.99 7 10.984 1.8 12.083l-1.8.399V48.53l1.8.4c5.1 1.098 9.2 5.192 10.3 10.284l.4 1.798h200.9l.4-1.798c1.1-5.092 5.2-9.186 10.3-10.285l1.8-.4V12.483l-1.8-.4c-5.1-1.098-9.2-5.092-10.3-10.285L213.4 0H12.5z'
                  ></path>
                </svg>

                <span className='absolute left-0 top-0 flex  h-full w-full items-center justify-center text-center font-fogtwono5 text-[15px] leading-[1.2] text-white lg_text-[36px] '>
                  GET NOW
                </span>
              </button>
            </div>
          </div>
        ) : null}

        {step === EnumStep.GetMyCharacterCard ? (
          <div className=' w-full max-w-site pb-[100px] lg_flex lg_h-full lg_min-h-[644px] lg_flex-col lg_justify-center lg_rounded-[24px] lg_bg-white lg_pb-0 lg_shadow-[0-2px_35px_0_rgba(0,0,0,0.05)] '>
            <div className='text-center font-fogtwono5 text-[28px] font-bold leading-[1.2] text-[#2469A9] lg_text-[48px]'>
              GET MY CHARACTER CARD
            </div>

            <div className='mt-[30px] flex flex-col flex-wrap items-center justify-center gap-y-5 lg_mt-[50px] lg_flex-row lg_gap-x-[90px]'>
              <button
                type='button'
                className='group relative'
                onClick={() => {
                  setStep(EnumStep.ChooseEventTheme);
                  document.documentElement.scrollTop = 0;
                  document.body.scrollTop = 0;
                }}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='273'
                  height='49'
                  fill='none'
                  viewBox='0 0 273 49'
                  className='lg_hidden'
                >
                  <path
                    fill='#2469A9'
                    d='M262.845 0v2.074c0 3.716 3.366 6.74 7.598 6.74h2.405v31.37h-2.309c-4.232 0-7.599 3.026-7.599 6.742V49H10.06v-2.074c0-3.716-3.463-6.741-7.6-6.741H.153V8.815h2.309c4.232 0 7.599-3.025 7.599-6.74V0zM11.118.95v1.124c0 4.235-3.848 7.692-8.657 7.692H1.21v29.468h1.25c4.81 0 8.658 3.457 8.658 7.692v1.123h250.668v-1.123c0-4.235 3.944-7.692 8.657-7.692h1.25V9.766h-1.25c-4.809 0-8.657-3.457-8.657-7.692V.95zM259.67 2.844l.289 1.296c.866 3.63 4.136 6.482 8.271 7.26l1.444.258v25.667l-1.444.259c-4.135.864-7.405 3.716-8.271 7.346l-.289 1.297H13.427l-.29-1.297c-.961-3.716-4.136-6.568-8.272-7.346l-1.442-.259v-25.58l1.442-.26c4.136-.864 7.407-3.716 8.273-7.345l.289-1.296z'
                  ></path>
                </svg>

                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='384'
                  height='70'
                  fill='none'
                  viewBox='0 0 384 70'
                  className='hidden lg_block'
                >
                  <path
                    className='fill-[#2469A9] lg_group-hover_fill-[#EB67AC]'
                    d='M369.913.5v2.92c0 5.233 4.741 9.493 10.7 9.493H384v44.174h-3.251c-5.96 0-10.7 4.26-10.7 9.492V69.5H13.951v-2.92c0-5.234-4.876-9.493-10.7-9.493H0V12.913h3.251c5.96 0 10.7-4.26 10.7-9.492V.5zM15.441 1.839V3.42c0 5.963-5.418 10.83-12.19 10.83h-1.76v41.497h1.76c6.772 0 12.19 4.868 12.19 10.831v1.582h352.982V66.58c0-5.963 5.553-10.83 12.19-10.831h1.762V14.251h-1.762c-6.772 0-12.19-4.867-12.19-10.83V1.839zm350.002 2.669.407 1.825c1.219 5.111 5.825 9.127 11.649 10.223l2.031.365v36.143l-2.031.365c-5.824 1.217-10.43 5.232-11.649 10.343l-.407 1.826H18.693l-.407-1.825c-1.354-5.233-5.824-9.249-11.648-10.344l-2.033-.366V17.042l2.033-.365c5.824-1.217 10.43-5.233 11.648-10.344l.406-1.825z'
                  ></path>
                </svg>

                <span className='absolute left-0 top-0 flex h-full w-full  items-center justify-center text-center font-inter text-[14px] font-light leading-[1.2] text-white lg_text-[24px]'>
                  Based on event themes
                </span>
              </button>
              <button
                type='button'
                className='group relative'
                onClick={() => {
                  setStep(EnumStep.ChooseDressStyle);
                  document.documentElement.scrollTop = 0;
                  document.body.scrollTop = 0;
                }}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='273'
                  height='49'
                  fill='none'
                  viewBox='0 0 273 49'
                  className='lg_hidden'
                >
                  <path
                    fill='#EB67AC'
                    d='M262.845 0v2.074c0 3.716 3.366 6.74 7.598 6.74h2.405v31.37h-2.309c-4.232 0-7.599 3.026-7.599 6.742V49H10.06v-2.074c0-3.716-3.463-6.741-7.6-6.741H.153V8.815h2.309c4.232 0 7.599-3.025 7.599-6.74V0zM11.118.95v1.124c0 4.235-3.848 7.692-8.657 7.692H1.21v29.468h1.25c4.81 0 8.658 3.457 8.658 7.692v1.123h250.668v-1.123c0-4.235 3.944-7.692 8.657-7.692h1.25V9.766h-1.25c-4.809 0-8.657-3.457-8.657-7.692V.95zM259.67 2.844l.289 1.296c.866 3.63 4.136 6.482 8.271 7.26l1.444.258v25.667l-1.444.259c-4.135.864-7.405 3.716-8.271 7.346l-.289 1.297H13.427l-.29-1.297c-.961-3.716-4.136-6.568-8.272-7.346l-1.442-.259v-25.58l1.442-.26c4.136-.864 7.407-3.716 8.273-7.345l.289-1.296z'
                  ></path>
                </svg>

                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='384'
                  height='70'
                  fill='none'
                  viewBox='0 0 384 70'
                  className=' hidden lg_block'
                >
                  <path
                    className='fill-[#EB67AC] lg_group-hover_fill-[#2469A9]'
                    d='M369.913.5v2.92c0 5.233 4.741 9.493 10.7 9.493H384v44.174h-3.251c-5.96 0-10.7 4.26-10.7 9.492V69.5H13.951v-2.92c0-5.234-4.876-9.493-10.7-9.493H0V12.913h3.251c5.96 0 10.7-4.26 10.7-9.492V.5zM15.441 1.839V3.42c0 5.963-5.418 10.83-12.19 10.83h-1.76v41.497h1.76c6.772 0 12.19 4.868 12.19 10.831v1.582h352.982V66.58c0-5.963 5.553-10.83 12.19-10.831h1.762V14.251h-1.762c-6.772 0-12.19-4.867-12.19-10.83V1.839zm350.002 2.669.407 1.825c1.219 5.111 5.825 9.127 11.649 10.223l2.031.365v36.143l-2.031.365c-5.824 1.217-10.43 5.232-11.649 10.343l-.407 1.826H18.693l-.407-1.825c-1.354-5.233-5.824-9.249-11.648-10.344l-2.033-.366V17.042l2.033-.365c5.824-1.217 10.43-5.233 11.648-10.344l.406-1.825z'
                  ></path>
                </svg>
                <span className='absolute left-0 top-0 flex h-full w-full  items-center justify-center text-center font-inter text-[14px] font-light leading-[1.2] text-white lg_text-[24px]'>
                  Based on dress styles
                </span>
              </button>
            </div>
          </div>
        ) : null}

        {step === EnumStep.ChooseEventTheme ? (
          <div className='w-full max-w-site px-4 py-10 lg_flex lg_h-full lg_min-h-[644px] lg_flex-col lg_justify-center lg_rounded-[24px] lg_bg-white lg_px-0 lg_py-0 lg_shadow-[0-2px_35px_0_rgba(0,0,0,0.05)] '>
            <div className='text-center font-fogtwono5 text-[28px] font-bold leading-[1.2] text-[#2469A9] lg_text-[48px]'>
              MY EVENT THEME IS...
            </div>

            <ul className='mt-[30px] flex flex-col items-center justify-center gap-y-5 lg_mt-[60px] lg_grid lg_grid-cols-2 lg_gap-x-[90px] lg_gap-y-[50px]'>
              {[
                'Black Tie',
                'Chic Cocktail',
                'Fairytale Party',
                'Vintage Soiree',
                'Cultural Party',
                'Royal Banquet',
                'Boho Festival',
                'Garden Party',
              ].map((label, i) => {
                return (
                  <li
                    key={i}
                    className={clsx('text-[0]', i % 2 === 0 ? 'lg_justify-end' : 'lg_justify-start', 'lg_flex')}
                  >
                    <button
                      type='button'
                      className='group relative'
                      onClick={() => {
                        handleSelectAnswer(i);
                      }}
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='273'
                        height='49'
                        fill='none'
                        viewBox='0 0 273 49'
                        className='lg_hidden'
                      >
                        <path
                          fill='#2469A9'
                          d='M262.845 0v2.074c0 3.716 3.366 6.74 7.598 6.74h2.405v31.37h-2.309c-4.232 0-7.599 3.026-7.599 6.742V49H10.06v-2.074c0-3.716-3.463-6.741-7.6-6.741H.153V8.815h2.309c4.232 0 7.599-3.025 7.599-6.74V0zM11.118.95v1.124c0 4.235-3.848 7.692-8.657 7.692H1.21v29.468h1.25c4.81 0 8.658 3.457 8.658 7.692v1.123h250.668v-1.123c0-4.235 3.944-7.692 8.657-7.692h1.25V9.766h-1.25c-4.809 0-8.657-3.457-8.657-7.692V.95zM259.67 2.844l.289 1.296c.866 3.63 4.136 6.482 8.271 7.26l1.444.258v25.667l-1.444.259c-4.135.864-7.405 3.716-8.271 7.346l-.289 1.297H13.427l-.29-1.297c-.961-3.716-4.136-6.568-8.272-7.346l-1.442-.259v-25.58l1.442-.26c4.136-.864 7.407-3.716 8.273-7.345l.289-1.296z'
                        ></path>
                      </svg>

                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='384'
                        height='70'
                        fill='none'
                        viewBox='0 0 384 70'
                        className='hidden lg_block'
                      >
                        <path
                          className='fill-[#2469A9] lg_group-hover_fill-[#EB67AC]'
                          d='M369.913.5v2.92c0 5.233 4.741 9.493 10.7 9.493H384v44.174h-3.251c-5.96 0-10.7 4.26-10.7 9.492V69.5H13.951v-2.92c0-5.234-4.876-9.493-10.7-9.493H0V12.913h3.251c5.96 0 10.7-4.26 10.7-9.492V.5zM15.441 1.839V3.42c0 5.963-5.418 10.83-12.19 10.83h-1.76v41.497h1.76c6.772 0 12.19 4.868 12.19 10.831v1.582h352.982V66.58c0-5.963 5.553-10.83 12.19-10.831h1.762V14.251h-1.762c-6.772 0-12.19-4.867-12.19-10.83V1.839zm350.002 2.669.407 1.825c1.219 5.111 5.825 9.127 11.649 10.223l2.031.365v36.143l-2.031.365c-5.824 1.217-10.43 5.232-11.649 10.343l-.407 1.826H18.693l-.407-1.825c-1.354-5.233-5.824-9.249-11.648-10.344l-2.033-.366V17.042l2.033-.365c5.824-1.217 10.43-5.233 11.648-10.344l.406-1.825z'
                        ></path>
                      </svg>

                      <span className='absolute left-0 top-0 flex h-full w-full  items-center justify-center text-center font-inter text-[14px] font-light leading-[1.2] text-white lg_text-[24px]'>
                        {label}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}

        {step === EnumStep.ChooseDressStyle ? (
          <div className='w-full max-w-site px-4 py-10 lg_flex lg_h-full lg_min-h-[644px] lg_flex-col lg_justify-center lg_rounded-[24px] lg_bg-white lg_px-0 lg_py-0 lg_shadow-[0-2px_35px_0_rgba(0,0,0,0.05)] '>
            <div className='text-center font-fogtwono5 text-[28px] font-bold leading-[1.2] text-[#2469A9] lg_text-[48px]'>
              MY FAVORITE DRESS STYLE IS...
            </div>

            <ul className='mt-[30px] flex flex-col items-center justify-center gap-y-5 lg_mt-[60px] lg_grid lg_grid-cols-2 lg_gap-x-[90px] lg_gap-y-[50px]'>
              {[
                'Classic Elegance',
                'Modern Minimalist',
                'Romantic Fairy',
                'Vintage Glamour',
                'Exotic Flair',
                'Luxurious Opulence',
                'Bohemian Style',
                'Easy Casual',
              ].map((label, i) => {
                return (
                  <li
                    key={i}
                    className={clsx('text-[0]', i % 2 === 0 ? 'lg_justify-end' : 'lg_justify-start', 'lg_flex')}
                  >
                    <button
                      type='button'
                      className='group relative'
                      onClick={() => {
                        handleSelectAnswer(i);
                      }}
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='273'
                        height='49'
                        fill='none'
                        viewBox='0 0 273 49'
                        className='lg_hidden'
                      >
                        <path
                          fill='#2469A9'
                          d='M262.845 0v2.074c0 3.716 3.366 6.74 7.598 6.74h2.405v31.37h-2.309c-4.232 0-7.599 3.026-7.599 6.742V49H10.06v-2.074c0-3.716-3.463-6.741-7.6-6.741H.153V8.815h2.309c4.232 0 7.599-3.025 7.599-6.74V0zM11.118.95v1.124c0 4.235-3.848 7.692-8.657 7.692H1.21v29.468h1.25c4.81 0 8.658 3.457 8.658 7.692v1.123h250.668v-1.123c0-4.235 3.944-7.692 8.657-7.692h1.25V9.766h-1.25c-4.809 0-8.657-3.457-8.657-7.692V.95zM259.67 2.844l.289 1.296c.866 3.63 4.136 6.482 8.271 7.26l1.444.258v25.667l-1.444.259c-4.135.864-7.405 3.716-8.271 7.346l-.289 1.297H13.427l-.29-1.297c-.961-3.716-4.136-6.568-8.272-7.346l-1.442-.259v-25.58l1.442-.26c4.136-.864 7.407-3.716 8.273-7.345l.289-1.296z'
                        ></path>
                      </svg>

                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='384'
                        height='70'
                        fill='none'
                        viewBox='0 0 384 70'
                        className='hidden lg_block'
                      >
                        <path
                          className='fill-[#2469A9] lg_group-hover_fill-[#EB67AC]'
                          d='M369.913.5v2.92c0 5.233 4.741 9.493 10.7 9.493H384v44.174h-3.251c-5.96 0-10.7 4.26-10.7 9.492V69.5H13.951v-2.92c0-5.234-4.876-9.493-10.7-9.493H0V12.913h3.251c5.96 0 10.7-4.26 10.7-9.492V.5zM15.441 1.839V3.42c0 5.963-5.418 10.83-12.19 10.83h-1.76v41.497h1.76c6.772 0 12.19 4.868 12.19 10.831v1.582h352.982V66.58c0-5.963 5.553-10.83 12.19-10.831h1.762V14.251h-1.762c-6.772 0-12.19-4.867-12.19-10.83V1.839zm350.002 2.669.407 1.825c1.219 5.111 5.825 9.127 11.649 10.223l2.031.365v36.143l-2.031.365c-5.824 1.217-10.43 5.232-11.649 10.343l-.407 1.826H18.693l-.407-1.825c-1.354-5.233-5.824-9.249-11.648-10.344l-2.033-.366V17.042l2.033-.365c5.824-1.217 10.43-5.233 11.648-10.344l.406-1.825z'
                        ></path>
                      </svg>

                      <span className='absolute left-0 top-0 flex h-full w-full  items-center justify-center text-center font-inter text-[14px] font-light leading-[1.2] text-white lg_text-[24px]'>
                        {label}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}

        {step === EnumStep.DrawDress ? (
          <div className='flex w-full max-w-site flex-col gap-x-[110px] gap-y-[30px] pb-[30px] lg_flex-row lg_items-end lg_py-[71px]'>
            <div className=' relative flex-shrink flex-grow-0 lg_basis-[800px] lg_bg-white lg_pb-12 lg_pt-[30px]'>
              <div className='relative text-[0]'>
                <picture>
                  <source
                    media='(max-width: 959px)'
                    srcSet={process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/card-placeholder-m.webp'}
                    width={750}
                    height={960}
                  />
                  <source
                    media='(min-width: 960px)'
                    srcSet={process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/card-placeholder.webp'}
                    width={800}
                    height={800}
                  />
                  <img
                    alt=''
                    className={clsx(
                      ' h-auto w-full lg_max-w-[800px]',
                      selectedCardSrc
                        ? 'opacity-0 transition-opacity duration-xs [transition-delay:1.8s]'
                        : 'opacity-100',
                    )}
                  />
                </picture>

                <div className='absolute left-0 top-0 h-full w-full overflow-hidden'>
                  <picture>
                    <source
                      media='(max-width: 959px)'
                      srcSet={configs[selectedAnswerIndex].mobileLichtSrc}
                      width={750}
                      height={960}
                    />
                    <source
                      media='(min-width: 960px)'
                      srcSet={configs[selectedAnswerIndex].lichtSrc}
                      width={800}
                      height={800}
                    />
                    <img
                      src={configs[selectedAnswerIndex].lichtSrc}
                      alt=''
                      className={clsx(
                        selectedCardSrc
                          ? 'opacity-100 transition-opacity duration-3xl [transition-delay:1.8s]'
                          : 'opacity-0',
                        'absolute left-0 top-0 h-auto w-[140%] lg_top-0 lg_w-full',
                      )}
                    />
                  </picture>
                </div>

                <div
                  className={clsx(
                    'absolute  left-1/2 top-[5%]  z-[2] w-[65.86%] -translate-x-1/2 lg_top-[11.25%] lg_w-[45.25%]',
                    '[perspective:1000px]',
                  )}
                >
                  <AspectRatio ratio={362 / 602}>
                    <img
                      src={process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/card.webp'}
                      width={362}
                      height={602}
                      alt=''
                      className={clsx(
                        'absolute left-0 top-0  h-auto w-full [backface-visibility:hidden] [transform-style:preserve-3d]',
                        selectedCardSrc
                          ? 'transition-transform [transform:rotateY(-180deg)] [transition-duration:2.4s]'
                          : '[transform:rotateY(0)]',
                      )}
                    />
                    <img
                      src={selectedCardSrc}
                      width={362}
                      height={602}
                      alt=''
                      className={clsx(
                        'absolute left-0 top-0  h-auto w-full [backface-visibility:hidden] [transform-style:preserve-3d] ',
                        selectedCardSrc
                          ? 'opacity-100 transition-transform [transform:rotateY(0)] [transition-duration:2.4s]'
                          : 'opacity-0 [transform:rotateY(180deg)]',
                      )}
                    />
                  </AspectRatio>
                </div>
              </div>

              <div
                className={clsx(
                  'flex w-full items-center justify-center gap-x-2.5 lg_absolute lg_bottom-[30px] lg_left-0 lg_gap-x-[33px]',
                  selectedCardSrc ? 'opacity-100 transition-opacity duration-3xl [transition-delay:1.8s]' : 'opacity-0',
                )}
              >
                <div className='group relative text-[0]'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='166'
                    height='49'
                    fill='none'
                    viewBox='0 0 166 49'
                    className='lg_hidden'
                  >
                    <path
                      fill='#2469A9'
                      d='M163.773 8.793c-3.688 0-6.737-3.05-6.737-6.737V0H8.76v2.056c0 3.688-3.05 6.737-6.737 6.737H-.033v31.414h2.056c3.688 0 6.737 3.05 6.737 6.737V49h148.276v-2.056c0-3.688 3.049-6.737 6.737-6.737h2.056V8.793zm1.134 30.492h-1.134c-4.255 0-7.659 3.475-7.659 7.659v1.134H9.682v-1.134c0-4.255-3.475-7.659-7.659-7.659H.89V9.715h1.134c4.255 0 7.659-3.475 7.659-7.659V.922h146.432v1.134c0 4.255 3.475 7.659 7.659 7.659h1.134z'
                    ></path>
                  </svg>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='161'
                    height='44'
                    fill='none'
                    viewBox='0 0 161 44'
                    className='absolute left-1/2 top-1/2 block -translate-x-1/2 -translate-y-1/2 lg_hidden'
                  >
                    <path
                      fill='#2469A9'
                      d='M8.58 1.276A9.42 9.42 0 0 1 1.276 8.58L0 8.864v25.6l1.276.283c3.617.78 6.524 3.687 7.304 7.304l.284 1.276h142.462l.283-1.276c.78-3.617 3.688-6.524 7.304-7.304l1.277-.284V8.864l-1.277-.284c-3.616-.78-6.524-3.616-7.304-7.304L151.326 0H8.864z'
                    ></path>
                  </svg>

                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='183'
                    height='54'
                    fill='none'
                    viewBox='0 0 183 54'
                    className='hidden lg_block'
                  >
                    <path
                      className='fill-[#2469A9] lg_group-hover_fill-[#EB67AC]'
                      d='M180.484 9.69c-4.063 0-7.424-3.36-7.424-7.424V0H9.654v2.266c0 4.064-3.36 7.424-7.424 7.424H-.037v34.62H2.23c4.063 0 7.424 3.36 7.424 7.424V54H173.06v-2.266c0-4.064 3.361-7.424 7.424-7.424h2.267V9.69zm1.251 33.604h-1.251c-4.688 0-8.44 3.829-8.44 8.44v1.25H10.67v-1.25c0-4.69-3.83-8.44-8.44-8.44H.98V10.706h1.25c4.689 0 8.44-3.829 8.44-8.44v-1.25h161.374v1.25c0 4.69 3.83 8.44 8.44 8.44h1.251z'
                    ></path>
                  </svg>

                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='177'
                    height='48'
                    fill='none'
                    viewBox='0 0 177 48'
                    className='absolute left-1/2  top-1/2 hidden -translate-x-1/2 -translate-y-1/2 lg_block'
                  >
                    <path
                      className='fill-[#2469A9] lg_group-hover_fill-[#EB67AC]'
                      d='M9.456 1.407a10.38 10.38 0 0 1-8.05 8.049L0 9.768V37.98l1.407.312c3.985.86 7.19 4.064 8.049 8.05l.312 1.406h156.999l.313-1.406c.859-3.986 4.063-7.19 8.049-8.05l1.406-.312V9.768l-1.406-.312c-3.986-.86-7.19-3.986-8.049-8.05L166.767 0H9.768z'
                    ></path>
                  </svg>

                  {productUrl && btnAvailable ? (
                    <Link
                      href={productUrl}
                      className={clsx(
                        'absolute left-1/2 top-1/2 flex h-full w-full -translate-x-1/2 -translate-y-1/2 items-center justify-center text-center font-inter text-[12px] font-light leading-[1.2] text-white lg_text-[13px]',
                      )}
                    >
                      SHOP THE DRESS
                    </Link>
                  ) : (
                    <span className='absolute left-1/2 top-1/2 block w-full -translate-x-1/2 -translate-y-1/2 cursor-default text-center font-inter text-[12px] font-light leading-[1.2] text-white lg_text-[13px]'>
                      SHOP THE DRESS
                    </span>
                  )}
                </div>
                <button
                  type='button'
                  className='group relative'
                  disabled={!btnAvailable}
                  onClick={() => {
                    if (!btnAvailable) return;

                    setShowSaved(true);

                    const selectedDownloadCardSrc =
                      selectedAnswerIndex === -1 || renderSelectedDressIndex === -1
                        ? ''
                        : configs[selectedAnswerIndex].images[renderSelectedDressIndex].downloadCardSrc;

                    if (selectedDownloadCardSrc) {
                      FileSaver.saveAs(selectedDownloadCardSrc);
                    }
                  }}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='166'
                    height='49'
                    fill='none'
                    viewBox='0 0 166 49'
                    className='lg_hidden'
                  >
                    <path
                      fill='#EB67AC'
                      d='M163.773 8.793c-3.688 0-6.737-3.05-6.737-6.737V0H8.76v2.056c0 3.688-3.05 6.737-6.737 6.737H-.033v31.414h2.056c3.688 0 6.737 3.05 6.737 6.737V49h148.276v-2.056c0-3.688 3.049-6.737 6.737-6.737h2.056V8.793zm1.134 30.492h-1.134c-4.255 0-7.659 3.475-7.659 7.659v1.134H9.682v-1.134c0-4.255-3.475-7.659-7.659-7.659H.89V9.715h1.134c4.255 0 7.659-3.475 7.659-7.659V.922h146.432v1.134c0 4.255 3.475 7.659 7.659 7.659h1.134z'
                    ></path>
                  </svg>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='161'
                    height='44'
                    fill='none'
                    viewBox='0 0 161 44'
                    className='absolute left-1/2 top-1/2 block -translate-x-1/2 -translate-y-1/2 lg_hidden'
                  >
                    <path
                      fill='#EB67AC'
                      d='M8.58 1.276A9.42 9.42 0 0 1 1.276 8.58L0 8.864v25.6l1.276.283c3.617.78 6.524 3.687 7.304 7.304l.284 1.276h142.462l.283-1.276c.78-3.617 3.688-6.524 7.304-7.304l1.277-.284V8.864l-1.277-.284c-3.616-.78-6.524-3.616-7.304-7.304L151.326 0H8.864z'
                    ></path>
                  </svg>

                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='183'
                    height='54'
                    fill='none'
                    viewBox='0 0 183 54'
                    className='hidden lg_block'
                  >
                    <path
                      className='fill-[#EB67AC] lg_group-hover_fill-[#2469A9]'
                      d='M180.484 9.69c-4.063 0-7.424-3.36-7.424-7.424V0H9.654v2.266c0 4.064-3.36 7.424-7.424 7.424H-.037v34.62H2.23c4.063 0 7.424 3.36 7.424 7.424V54H173.06v-2.266c0-4.064 3.361-7.424 7.424-7.424h2.267V9.69zm1.251 33.604h-1.251c-4.688 0-8.44 3.829-8.44 8.44v1.25H10.67v-1.25c0-4.69-3.83-8.44-8.44-8.44H.98V10.706h1.25c4.689 0 8.44-3.829 8.44-8.44v-1.25h161.374v1.25c0 4.69 3.83 8.44 8.44 8.44h1.251z'
                    ></path>
                  </svg>

                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='177'
                    height='48'
                    fill='none'
                    viewBox='0 0 177 48'
                    className='absolute left-1/2  top-1/2 hidden -translate-x-1/2 -translate-y-1/2 lg_block'
                  >
                    <path
                      className='fill-[#EB67AC] lg_group-hover_fill-[#2469A9]'
                      d='M9.456 1.407a10.38 10.38 0 0 1-8.05 8.049L0 9.768V37.98l1.407.312c3.985.86 7.19 4.064 8.049 8.05l.312 1.406h156.999l.313-1.406c.859-3.986 4.063-7.19 8.049-8.05l1.406-.312V9.768l-1.406-.312c-3.986-.86-7.19-3.986-8.049-8.05L166.767 0H9.768z'
                    ></path>
                  </svg>

                  <span className='absolute left-1/2 top-1/2 block w-full -translate-x-1/2 -translate-y-1/2 text-center font-inter text-[12px] font-light leading-[1.2] text-white lg_text-[13px]'>
                    DOWNLOAD THE CARD
                  </span>
                </button>
              </div>
            </div>
            <div className='flex-shrink flex-grow-0 lg_basis-[530px]'>
              <div className='text-center font-fogtwono5 text-[20px] leading-[1.2] text-[#2469A9] lg_text-left lg_text-[24px]'>
                {configs[selectedAnswerIndex]?.label}
              </div>
              <ul className='mt-[14px] flex gap-x-[5px] px-4 lg_mt-[30px] lg_grid lg_grid-cols-2 lg_gap-x-[30px] lg_gap-y-5 lg_px-0'>
                {configs[selectedAnswerIndex]?.images.map(({ placeholderSrc }, i) => {
                  return (
                    <li
                      key={placeholderSrc}
                      className={clsx(
                        'cursor-pointer',
                        'basis-1/4 border-[1px] border-solid lg_border-[3px]',
                        selectedDressIndex === i ? 'border-[#EB67AC]' : 'border-transparent',
                      )}
                      onClick={() => {
                        if (selectedDressIndex === i) return;

                        document.documentElement.scrollTop = 0;
                        document.body.scrollTop = 0;
                        setSelectedDressIndex(i);
                        setRenderSelectedDressIndex(-1);
                        setBtnAvailable(false);
                        setTimeout(() => {
                          setRenderSelectedDressIndex(i);
                        }, 300);
                        setTimeout(() => {
                          setBtnAvailable(true);
                        }, 2400);
                      }}
                    >
                      <img
                        src={placeholderSrc}
                        alt=''
                        width={1022}
                        height={1500}
                        className='h-auto w-full'
                      />
                    </li>
                  );
                })}
                <li></li>
              </ul>
            </div>
          </div>
        ) : null}

        <Dialog
          open={showSaved}
          onOpenChange={setShowSaved}
        >
          <DialogContent
            showCloseButton={false}
            className='block h-screen w-full gap-0 overflow-auto border-none bg-transparent p-0 shadow-none outline-none supports-[height:100dvh]_h-[100dvh] lg_max-w-[600px]'
          >
            <DialogTitle className='hidden'>saved</DialogTitle>
            <div className='flex h-full flex-col flex-nowrap justify-center overflow-auto'>
              <div
                className={clsx('flex max-h-full flex-col items-center gap-y-2.5 lg_gap-y-4', 'bg-cover bg-no-repeat')}
                style={{
                  backgroundImage: `url(${process.env.NEXT_PUBLIC_BASE_PATH + (isMobile ? '/images/who-is-your-character/star-bg-m.webp' : '/images/who-is-your-character/star-bg.webp')})`,
                }}
              >
                <div className='text-center font-inter text-[16px] leading-[1.2] text-white lg_text-[28px]'>SAVED</div>
                <img
                  alt=''
                  width={450}
                  height={747}
                  src={selectedCardSrc}
                  className='h-auto w-full max-w-[74.6%] lg_max-w-[450px]'
                />
                <div className='flex flex-wrap items-end justify-center gap-2.5 lg_gap-x-[33px]'>
                  <div className='flex flex-col items-start'>
                    <div className='relative '>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='102'
                        height='21'
                        fill='none'
                        viewBox='0 0 102 21'
                        className='lg_hidden'
                      >
                        <path
                          fill='#EB67AC'
                          d='M93 0a9 9 0 1 1 0 18H54.436a2 2 0 0 1-.37.587l-1.578 1.757a2 2 0 0 1-2.975 0l-1.578-1.757a2 2 0 0 1-.37-.587H9A9 9 0 1 1 9 0z'
                        ></path>
                      </svg>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='116'
                        height='24'
                        fill='none'
                        viewBox='0 0 116 24'
                        className='hidden lg_block'
                      >
                        <path
                          fill='#EB67AC'
                          d='M105.819 0c5.423 0 9.819 4.396 9.819 9.818 0 5.423-4.396 9.819-9.819 9.819H61.71c-.05.415-.225.828-.547 1.186l-1.72 1.916a2.183 2.183 0 0 1-3.248 0l-1.72-1.916a2.15 2.15 0 0 1-.546-1.186H9.819C4.395 19.637 0 15.24 0 9.818 0 4.396 4.396 0 9.818 0z'
                        ></path>
                      </svg>

                      <span className='absolute left-1/2 top-0.5 block w-full -translate-x-1/2 text-center font-inter text-[12px] leading-[1.2] text-white lg_text-[14px]'>
                        WIN SURPRISE
                      </span>
                    </div>
                    <a
                      href='https://www.instagram.com'
                      className='group relative block text-[0]'
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='149'
                        height='44'
                        fill='none'
                        viewBox='0 0 149 44'
                        className='lg_hidden'
                      >
                        <path
                          fill='#2469A9'
                          d='M147.061 7.896c-3.311 0-6.049-2.738-6.049-6.05V0H7.866v1.847c0 3.31-2.738 6.049-6.05 6.049H-.03v28.208h1.847c3.31 0 6.049 2.738 6.049 6.05V44h133.146v-1.847c0-3.31 2.738-6.049 6.049-6.049h1.847V7.896zm1.019 27.38h-1.019c-3.82 0-6.877 3.12-6.877 6.877v1.02H8.694v-1.02c0-3.82-3.12-6.877-6.877-6.877H.797V8.724h1.02c3.82 0 6.877-3.12 6.877-6.877V.827h131.49v1.02c0 3.82 3.12 6.877 6.877 6.877h1.019z'
                        ></path>
                      </svg>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='144'
                        height='39'
                        fill='none'
                        viewBox='0 0 144 39'
                        className='absolute left-1/2 top-1/2 block -translate-x-1/2 -translate-y-1/2 lg_hidden'
                      >
                        <path
                          fill='#2469A9'
                          d='M7.705 1.146a8.46 8.46 0 0 1-6.559 6.559L0 7.959v22.988l1.146.254c3.248.7 5.858 3.311 6.559 6.559l.254 1.146h127.925l.255-1.146c.7-3.248 3.311-5.858 6.559-6.559l1.146-.255V7.96l-1.146-.254c-3.248-.7-5.859-3.248-6.559-6.559L135.884 0H7.959z'
                        ></path>
                      </svg>

                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='183'
                        height='54'
                        fill='none'
                        viewBox='0 0 183 54'
                        className='hidden lg_block'
                      >
                        <path
                          className='fill-[#2469A9] lg_group-hover_fill-[#EB67AC]'
                          d='M180.484 9.69c-4.063 0-7.424-3.36-7.424-7.424V0H9.654v2.266c0 4.064-3.36 7.424-7.424 7.424H-.037v34.62H2.23c4.063 0 7.424 3.36 7.424 7.424V54H173.06v-2.266c0-4.064 3.361-7.424 7.424-7.424h2.267V9.69zm1.251 33.604h-1.251c-4.688 0-8.44 3.829-8.44 8.44v1.25H10.67v-1.25c0-4.69-3.83-8.44-8.44-8.44H.98V10.706h1.25c4.689 0 8.44-3.829 8.44-8.44v-1.25h161.374v1.25c0 4.69 3.83 8.44 8.44 8.44h1.251z'
                        ></path>
                      </svg>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='177'
                        height='48'
                        fill='none'
                        viewBox='0 0 177 48'
                        className='absolute left-1/2 top-1/2  hidden -translate-x-1/2 -translate-y-1/2 lg_block'
                      >
                        <path
                          className='fill-[#2469A9] lg_group-hover_fill-[#EB67AC]'
                          d='M9.456 1.407a10.38 10.38 0 0 1-8.05 8.049L0 9.768V37.98l1.407.312c3.985.86 7.19 4.064 8.049 8.05l.312 1.406h156.999l.313-1.406c.859-3.986 4.063-7.19 8.049-8.05l1.406-.312V9.768l-1.406-.312c-3.986-.86-7.19-3.986-8.049-8.05L166.767 0H9.768z'
                        ></path>
                      </svg>

                      <span className='absolute left-0 top-1/2 block w-full -translate-y-1/2 text-center font-inter text-[12px] font-light leading-[1.2] text-white lg_text-[15px] '>
                        SHARE WITH FRIEND
                      </span>
                    </a>
                  </div>

                  <button
                    type='button'
                    onClick={() => {
                      setShowSaved(false);
                    }}
                    className='group relative text-[0]'
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='149'
                      height='44'
                      fill='none'
                      viewBox='0 0 149 44'
                      className='lg_hidden'
                    >
                      <path
                        fill='#EB67AC'
                        d='M147.061 7.896c-3.311 0-6.049-2.738-6.049-6.05V0H7.866v1.847c0 3.31-2.738 6.049-6.05 6.049H-.03v28.208h1.847c3.31 0 6.049 2.738 6.049 6.05V44h133.146v-1.847c0-3.31 2.738-6.049 6.049-6.049h1.847V7.896zm1.019 27.38h-1.019c-3.82 0-6.877 3.12-6.877 6.877v1.02H8.694v-1.02c0-3.82-3.12-6.877-6.877-6.877H.797V8.724h1.02c3.82 0 6.877-3.12 6.877-6.877V.827h131.49v1.02c0 3.82 3.12 6.877 6.877 6.877h1.019z'
                      ></path>
                    </svg>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='144'
                      height='39'
                      fill='none'
                      viewBox='0 0 144 39'
                      className='absolute left-1/2 top-1/2 block -translate-x-1/2 -translate-y-1/2 lg_hidden'
                    >
                      <path
                        fill='#EB67AC'
                        d='M7.705 1.146a8.46 8.46 0 0 1-6.559 6.559L0 7.959v22.988l1.146.254c3.248.7 5.858 3.311 6.559 6.559l.254 1.146h127.925l.255-1.146c.7-3.248 3.311-5.858 6.559-6.559l1.146-.255V7.96l-1.146-.254c-3.248-.7-5.859-3.248-6.559-6.559L135.884 0H7.959z'
                      ></path>
                    </svg>

                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='183'
                      height='54'
                      fill='none'
                      viewBox='0 0 183 54'
                      className='hidden lg_block'
                    >
                      <path
                        className='fill-[#EB67AC] lg_group-hover_fill-[#2469A9]'
                        d='M180.484 9.69c-4.063 0-7.424-3.36-7.424-7.424V0H9.654v2.266c0 4.064-3.36 7.424-7.424 7.424H-.037v34.62H2.23c4.063 0 7.424 3.36 7.424 7.424V54H173.06v-2.266c0-4.064 3.361-7.424 7.424-7.424h2.267V9.69zm1.251 33.604h-1.251c-4.688 0-8.44 3.829-8.44 8.44v1.25H10.67v-1.25c0-4.69-3.83-8.44-8.44-8.44H.98V10.706h1.25c4.689 0 8.44-3.829 8.44-8.44v-1.25h161.374v1.25c0 4.69 3.83 8.44 8.44 8.44h1.251z'
                      ></path>
                    </svg>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='177'
                      height='48'
                      fill='none'
                      viewBox='0 0 177 48'
                      className='absolute left-1/2 top-1/2  hidden -translate-x-1/2 -translate-y-1/2 lg_block'
                    >
                      <path
                        className='fill-[#EB67AC] lg_group-hover_fill-[#2469A9]'
                        d='M9.456 1.407a10.38 10.38 0 0 1-8.05 8.049L0 9.768V37.98l1.407.312c3.985.86 7.19 4.064 8.049 8.05l.312 1.406h156.999l.313-1.406c.859-3.986 4.063-7.19 8.049-8.05l1.406-.312V9.768l-1.406-.312c-3.986-.86-7.19-3.986-8.049-8.05L166.767 0H9.768z'
                      ></path>
                    </svg>
                    <span className='absolute left-0 top-1/2 block w-full -translate-y-1/2 text-center font-inter text-[12px] font-light leading-[1.2] text-white lg_text-[15px]'>
                      OKAY
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

const configs: {
  label: string;
  lichtSrc: string;
  mobileLichtSrc: string;
  images: {
    placeholderSrc: string;
    cardSrc: string;
    downloadCardSrc: string;
    productUrl: string;
  }[];
}[] = [
  {
    label: "QUEEN'S DRESSES",
    images: [
      {
        placeholderSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/queen/lf25136cp.jpg',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/queen/lf25136cp-card.webp',
        downloadCardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/queen/lf25136cp-card.png',
        productUrl: '/aw-shannon-dress-lf25136cp.html',
      },
      {
        placeholderSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/queen/lfa25354cp.webp',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/queen/lfa25354cp-card.webp',
        downloadCardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/queen/lfa25354cp-card.png',
        productUrl: '/aw-suzy-dress-lfa25354cp.html',
      },

      {
        placeholderSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/queen/lfa25357cp.jpg',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/queen/lfa25357cp-card.webp',
        downloadCardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/queen/lfa25357cp-card.png',
        productUrl: '/aw-valeria-dress-lfa25357cp.html',
      },
      {
        placeholderSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/queen/lfa25175cp.jpg',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/queen/lfa25175cp-card.webp',
        downloadCardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/queen/lfa25175cp-card.png',
        productUrl: '/aw-sadia-dress-lf25175cp.html',
      },
    ],
    lichtSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/queen/licht.webp',
    mobileLichtSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/queen/licht-m.webp',
  },
  {
    label: "FASHIONISTA'S DRESSES",
    images: [
      {
        placeholderSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/fashionista/lf24048cp.webp',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/fashionista/lf24048cp-card.webp',
        downloadCardSrc:
          process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/fashionista/lf24048cp-card.png',
        productUrl: '/aw-marci-dress-lf24048cp.html',
      },
      {
        placeholderSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/fashionista/lf25028cp.jpg',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/fashionista/lf25028cp-card.webp',
        downloadCardSrc:
          process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/fashionista/lf25028cp-card.png',
        productUrl: '/aw-lia-dress-lf25028cp.html',
      },
      {
        placeholderSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/fashionista/lf25126cp.jpg',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/fashionista/lf25126cp-card.webp',
        downloadCardSrc:
          process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/fashionista/lf25126cp-card.png',
        productUrl: '/aw-eleri-dress-lf25126cp.html',
      },
      {
        placeholderSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/fashionista/lf25062cp.jpg',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/fashionista/lf25062cp-card.webp',
        downloadCardSrc:
          process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/fashionista/lf25062cp-card.png',
        productUrl: '/aw-caitlyn-dress-lf25062cp.html',
      },
    ],
    lichtSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/fashionista/licht.webp',
    mobileLichtSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/fashionista/licht-m.webp',
  },
  {
    label: "ENCHANTRESS'S DRESSES",
    images: [
      {
        placeholderSrc:
          process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/enchantress/lfma25272cp.webp',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/enchantress/lfma25272cp-card.webp',
        downloadCardSrc:
          process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/enchantress/lfma25272cp-card.png',
        productUrl: '/aw-anoushka-dress-lfma25272cp.html',
      },
      {
        placeholderSrc:
          process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/enchantress/lfma25276cp.webp',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/enchantress/lfma25276cp-card.webp',
        downloadCardSrc:
          process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/enchantress/lfma25276cp-card.png',
        productUrl: '/aw-saba-dress-lfma25276cp.html',
      },
      {
        placeholderSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/enchantress/lf24230cp.webp',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/enchantress/lf24230cp-card.webp',
        downloadCardSrc:
          process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/enchantress/lf24230cp-card.png',
        productUrl: '/aw-thetis-dress-lf24230cp.html',
      },
      {
        placeholderSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/enchantress/lf25224cp.webp',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/enchantress/lf25224cp-card.webp',
        downloadCardSrc:
          process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/enchantress/lf25224cp-card.png',
        productUrl: '/aw-kirsty-dress-lf25224cp.html',
      },
    ],
    lichtSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/enchantress/licht.webp',
    mobileLichtSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/enchantress/licht-m.webp',
  },
  {
    label: "DIVA'S DRESSES",
    images: [
      {
        placeholderSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/diva/lf25225cp.webp',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/diva/lf25225cp-card.webp',
        downloadCardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/diva/lf25225cp-card.png',
        productUrl: '/aw-colin-dress-lf25225cp.html',
      },
      {
        placeholderSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/diva/lfa25160cp.webp',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/diva/lfa25160cp-card.webp',
        downloadCardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/diva/lfa25160cp-card.png',
        productUrl: '/aw-cleo-dress-lf25160cp.html',
      },
      {
        placeholderSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/diva/lf25074cp.webp',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/diva/lf25074cp-card.webp',
        downloadCardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/diva/lf25074cp-card.png',
        productUrl: '/aw-rio-dress-lf25074cp.html',
      },
      {
        placeholderSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/diva/lfma25289cp.webp',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/diva/lfma25289cp-card.webp',
        downloadCardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/diva/lfma25289cp-card.png',
        productUrl: '/aw-safiya-dress-lfma25289cp.html',
      },
    ],
    lichtSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/diva/licht.webp',
    mobileLichtSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/diva/licht-m.webp',
  },
  {
    label: "ADVENTURER'S DRESSES",
    images: [
      {
        placeholderSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/adventurer/lfa25164cp.webp',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/adventurer/lfa25164cp-card.webp',
        downloadCardSrc:
          process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/adventurer/lfa25164cp-card.png',
        productUrl: '/aw-aneesa-dress-lf25164cp.html',
      },
      {
        placeholderSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/adventurer/lfma25209cp.webp',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/adventurer/lfma25209cp-card.webp',
        downloadCardSrc:
          process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/adventurer/lfma25209cp-card.png',
        productUrl: '/aw-zora-dress-lfma25209cp.html',
      },
      {
        placeholderSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/adventurer/lfa25179cp.webp',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/adventurer/lfa25179cp-card.webp',
        downloadCardSrc:
          process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/adventurer/lfa25179cp-card.png',
        productUrl: '/aw-brodie-dress-lf25179cp.html',
      },
      {
        placeholderSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/adventurer/lf23231cp.webp',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/adventurer/lf23231cp-card.webp',
        downloadCardSrc:
          process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/adventurer/lf23231cp-card.png',
        productUrl: '/aw-carolyn-dress-lf23231cp.html',
      },
    ],
    lichtSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/adventurer/licht.webp',
    mobileLichtSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/adventurer/licht-m.webp',
  },
  {
    label: "EMPRESS'S DRESSES",
    images: [
      {
        placeholderSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/empress/lf25251cp.webp',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/empress/lf25251cp-card.webp',
        downloadCardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/empress/lf25251cp-card.png',
        productUrl: '/aw-danielle-dress-lf25251cp.html',
      },
      {
        placeholderSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/empress/lf25241cp.webp',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/empress/lf25241cp-card.webp',
        downloadCardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/empress/lf25241cp-card.png',
        productUrl: '/aw-kayleigh-dress-lf25241cp.html',
      },
      {
        placeholderSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/empress/lf25254cp.webp',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/empress/lf25254cp-card.webp',
        downloadCardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/empress/lf25254cp-card.png',
        productUrl: '/aw-leia-dress-lf25254cp.html',
      },
      {
        placeholderSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/empress/lfma25264cp.webp',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/empress/lfma25264cp-card.webp',
        downloadCardSrc:
          process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/empress/lfma25264cp-card.png',
        productUrl: '/aw-neha-dress-lfma25264cp.html',
      },
    ],
    lichtSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/empress/licht.webp',
    mobileLichtSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/empress/licht-m.webp',
  },
  {
    label: "FREE SPIRIT'S DRESSES",
    images: [
      {
        placeholderSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/free-spirit/lfa25164cp.webp',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/free-spirit/lfa25164cp-card.webp',
        downloadCardSrc:
          process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/free-spirit/lfa25164cp-card.png',
        productUrl: '/aw-aneesa-dress-lf25164cp.html',
      },
      {
        placeholderSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/free-spirit/lfa25162cp.webp',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/free-spirit/lfa25162cp-card.webp',
        downloadCardSrc:
          process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/free-spirit/lfa25162cp-card.png',
        productUrl: '/aw-darcie-dress-lf25162cp.html',
      },
      {
        placeholderSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/free-spirit/lf24223cp.webp',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/free-spirit/lf24223cp-card.webp',
        downloadCardSrc:
          process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/free-spirit/lf24223cp-card.png',
        productUrl: '/aw-alivia-dress-lf24223cp.html',
      },
      {
        placeholderSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/free-spirit/lf25180cp.webp',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/free-spirit/lf25180cp-card.webp',
        downloadCardSrc:
          process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/free-spirit/lf25180cp-card.png',
        productUrl: '/aw-chanel-multi-color-dress-lf25180cp.html',
      },
    ],
    lichtSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/free-spirit/licht.webp',
    mobileLichtSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/free-spirit/licht-m.webp',
  },
  {
    label: "TRENDSETTER'S DRESSES",
    images: [
      {
        placeholderSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/trendsetter/lfa25368cp.webp',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/trendsetter/lfa25368cp-card.webp',
        downloadCardSrc:
          process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/trendsetter/lfa25368cp-card.png',
        productUrl: '/aw-rebecca-dress-lfa25368cp.html',
      },
      {
        placeholderSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/trendsetter/lf24097cp.webp',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/trendsetter/lf24097cp-card.webp',
        downloadCardSrc:
          process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/trendsetter/lf24097cp-card.png',
        productUrl: '/aw-yasmin-dress-lf24097cp.html',
      },
      {
        placeholderSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/trendsetter/lfa25172cp.webp',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/trendsetter/lfa25172cp-card.webp',
        downloadCardSrc:
          process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/trendsetter/lfa25172cp-card.png',
        productUrl: '/aw-hermione-dress-lf25172cp.html',
      },
      {
        placeholderSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/trendsetter/lfa25356cp.webp',
        cardSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/trendsetter/lfa25356cp-card.webp',
        downloadCardSrc:
          process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/trendsetter/lfa25356cp-card.png',
        productUrl: '/aw-hailey-dress-lfa25356cp.html',
      },
    ],
    lichtSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/trendsetter/licht.webp',
    mobileLichtSrc: process.env.NEXT_PUBLIC_BASE_PATH + '/images/who-is-your-character/trendsetter/licht-m.webp',
  },
];

export default WhoIsYourCharacterPage;
